import React, { useEffect, useMemo, useState, useRef, createElement } from "react";
import { Modal, Input, DatePicker, Select, Checkbox, ConfigProvider, Form, Button, message, Cascader, Tooltip, Tag } from 'antd'
import { CloseSvg, DragSvg, RemoveSvg, CloudSvg } from "@/components/svgs"
import {
    createControlNodeLineAPI, updateControlNodeLineAPI, getControlNodeLineDetailAPI, deleteControlNodeLineAPI,
    getPlmProjectAllTeamUserApi, getControlTemplateNodesAPI, updateControlLineSortAPI, updateControlNodeLineMilestoneTimeAPI
} from "@/services"
import { useRequest } from "ahooks"
import dayjs from "dayjs";
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { getNodeStatusColor, k_renderLineType } from "./util"
import type { Option, PayLineData, DefaultNode } from "./util"
import clsx from "clsx";
import { documentUtil, toHsl, parseTime, generateRandomColor } from "@/utils"
import './index.scss'

interface IProps {
    visible: boolean;
    onClose: () => void;
    onUpdate: () => void;
    onShowFileModal: (nodeID: number) => void;
    list: API.PROJECT.ControlNodeLine[]
    milestones?: API.PROJECT.Milestone[]
    paymentLines?: PayLineData[]
    allStatus: API.PROJECT.ControlNodeStatus[]
    defaultID?: number | string | null,
    forbidEdit?: boolean
}
const initNodeLine: Partial<API.PROJECT.ControlNodeLine> = {
    lineName: "",
    colorValue: "",
    assignUserId: 0,
}
interface RenderLinkNode{
    ID: number | string,
    lineId: number | string,
    type?: string
}
type RenderLineNode = Request.ControlNode & { ID?: number, renderLinkNodes?: RenderLinkNode[], hasDDSFile?: boolean }
interface CascaderOption {
    value: string | number;
    label: React.ReactNode;
    type?: string;
    children?: CascaderOption[];
}
const Index: React.FC<IProps> = (props) => {
    const [modalApi, modalContextHolder] = Modal.useModal();
    const [messageApi, contextHolder] = message.useMessage();
    const params = useParams()
    const projectID = params.projectID || ""
    const { loading: isCreating, run: createControlNodeLine } = useRequest(createControlNodeLineAPI, {
        manual: true,
        onSuccess: () => {
            props.onClose()
            props.onUpdate()
        },
        onError(msg) {
            messageApi.error("新建任务线失败~")
        },
    })
    const { loading: isUpdating, run: updateControlNodeLine } = useRequest(updateControlNodeLineAPI, {
        manual: true,
        onSuccess: () => {
            props.onClose()
            props.onUpdate()
        },
        onError(msg) {
            messageApi.error("更新任务线失败~")
        },
    })
    const { run: fetchControlNodeLine } = useRequest(getControlNodeLineDetailAPI, {
        manual: true,
    })
    
    const { runAsync: deleteControlNodeLine } = useRequest(deleteControlNodeLineAPI, {
        manual: true,
        onSuccess: () => {
            props.onUpdate()
        },
        onError(msg) {
            messageApi.error("删除任务线失败~")
        },
    })
    const { runAsync: updateControlNodeLineMilestoneTime } = useRequest(updateControlNodeLineMilestoneTimeAPI, {
        manual: true,
        debounceWait: 300,
        onSuccess: () => {
            props.onUpdate()
            message.success("同步里程碑时间成功")
        },
        onError(msg) {
            messageApi.error("同步里程碑时间失败~")
        },
    })
    
    const [currentNodeLine, setCurrentNodeLine] = useState<Partial<API.PROJECT.ControlNodeLine>>({
        ...initNodeLine
    })
    
    const isEditNotSaved = () => {
        if(!currentNodeLine) return false
        if (currentNodeLine.ID) {
            const prevLine = props.list.find(v => v.ID === currentNodeLine.ID)
            if(!prevLine) return false
            const prevNodes = (prevLine.nodes || []).map(v => {
                return {
                    date: v.date,
                    isDds: v.isDds,
                    isDraw: v.isDraw,
                    nodeName: v.nodeName,
                    status: v.status || 0,
                    nodes: v.nodes ? v.nodes.map(n => ({ ID: n.ID })) : [],
                    pmNodes: v.pmNodes ? v.pmNodes.map(n => ({ code: n.code })) : [],
                }
            })
            if (currentNodeLine.lineName !== prevLine.lineName ||
                currentNodeLine.assignUserId !== prevLine.assignUserId ||
                currentNodeLine.colorValue !== prevLine.colorValue) {
                return true
            }

            const nodes = formList.map(v => {
                let nodes: any[] = [], pmNodes: any[] = []
                const { renderLinkNodes, ...item } = v
                renderLinkNodes?.forEach(n => {
                    if (n.type === k_renderLineType.default) {
                        nodes.push({ ID: n.ID })
                    } else {
                        pmNodes.push({ code: n.ID })
                    }
                })
                return {
                    date: item.date,
                    isDds: item.isDds,
                    isDraw: item.isDraw,
                    nodeName: item.nodeName,
                    status: item.status || 0,
                    nodes: nodes ? nodes.map(n => ({ ID: n.ID })) : [],
                    pmNodes: pmNodes ? pmNodes.map(n => ({ code: n.code })) : [],
                }
            })
            if (JSON.stringify(nodes) !== JSON.stringify(prevNodes)) {
                return true
            }
            return false
        }
        if (currentNodeLine.lineName !== "" || currentNodeLine.assignUserId || formList.length > 0) {
            return true
        }
        return false  
    }
    const onDDSFileClick = (item: RenderLineNode) => {
        if (!item.ID) return
        props.onShowFileModal(item.ID)
    }
    const onChangeTab = (item: API.PROJECT.ControlNodeLine, ignore = false) => {
        const changeTab = () => {
            setCurrentNodeLine({ ...item })
            setFormList((item.nodes || []).map(v => {
                let linkNodes: RenderLinkNode[] = []
                if (v.nodes) {
                    const defaultNodes = v.nodes.map(n => ({ ID: n.ID, lineId: n.lineId, type: k_renderLineType.default }))
                    linkNodes.push(...defaultNodes)
                }
                if (v.pmNodes) {
                    const pmNodes = v.pmNodes.map(n => {
                        return { ID: n.code, lineId: n.lineId, type: n.type }
                    })
                    linkNodes.push(...pmNodes)
                }
                return {
                    ID: v.ID,
                    date: v.date,
                    isDds: v.isDds,
                    isDraw: v.isDraw,
                    milestoneCode: v.milestoneCode,
                    nodeId: v.nodeId,
                    nodeName: v.nodeName,
                    status: v.status || 0,
                    nodes: v.nodes ? v.nodes.map(n => ({ ID: n.ID, lineId: n.lineId })) : [],
                    renderLinkNodes: linkNodes,
                    hasDDSFile: v.nodeFiles && v.nodeFiles.length > 0,
                }

            }))
        }
        if (!ignore && isEditNotSaved()) {
            modalApi.confirm({
                title: null,
                icon: null,
                content: <span className="text-[13px]">切换将导致已填写未保存数据丢失，是否继续切换？</span>,
                okButtonProps: {
                    danger: true,
                    type: "primary"
                },
                onOk() {
                    changeTab()
                }
            });
            return
        }
        changeTab()
    }
    const templateMap = useRef<Record<string, API.PROJECT.ControlTemplateNode[]>>({})
    useEffect(() => {
        const templateLineId = currentNodeLine.templateLineId || ""
        if (templateLineId && !templateMap.current[templateLineId]) {
            getControlTemplateNodesAPI(templateLineId).then(res => {
                templateMap.current[templateLineId] = res
                // 这里可以安全地使用 currentNodeLine，因为它在 effect 依赖中
                // setFormList(res.map(v => ({
                //     nodeName: v.name,
                //     date: "",
                //     status: 0,
                //     isDraw: false,
                //     isDds: false,
                //     templateLineId: v.templateLineId,
                //     templateNodeId: v.ID
                // })));
            });
        }
    }, [currentNodeLine]);
    const onImportTemplate = (templateLineId: number | undefined) => {
        if (!templateLineId || !templateMap.current[templateLineId]) return
        const template = templateMap.current[templateLineId]
        const ids = formList.filter(v => !!v.templateNodeId).map(v => v.templateNodeId)
        const idSet = new Set(ids)
        const newNodes = template.filter(v => !idSet.has(v.ID)).map(v => ({
            nodeName: v.name,
            date: "",
            status: 0,
            isDraw: false,
            isDds: false,
            templateLineId: v.templateLineId,
            templateNodeId: v.ID
        }))
        setFormList([...formList, ...newNodes])
    }
    const onChangeCreateTab = () => {
        const color = generateRandomColor()
        const newInitNodeLine = {
            ...initNodeLine,
            colorValue: color,
        }
        if (isEditNotSaved()) {
            modalApi.confirm({
                title: null,
                icon: null,
                content: <span className="text-[13px]">切换将导致已填写未保存数据丢失，是否继续切换？</span>,
                okButtonProps: {
                    danger: true,
                    type: "primary"
                },
                onOk() {
                    setCurrentNodeLine({ ...newInitNodeLine })
                    setFormList([])
                }
            });
            return
        }
        setCurrentNodeLine({ ...newInitNodeLine })
        setFormList([])
    }
    useEffect(() => {
        if (!props.visible) return
        setRenderList(props.list)
        if (props.list.length > 0) {
            let item = props.list[0]
            if (props.defaultID) {
                const defaultItem = props.list.find(v => v.ID === props.defaultID)
                if(defaultItem) item = defaultItem
            }
            onChangeTab(item, true)
        } else {
            onChangeCreateTab()
        }
    }, [props.visible, props.list])
    const [formList, setFormList] = useState<RenderLineNode[]>([])
    const onFormChange = (field: string, value: any, index: number, item?: RenderLineNode) => {
        if (currentNodeLine.ID && field === "status" && value === 1 && item && item.ID && item.isDds) {
            //修改状态为已完成
            const node = currentNodeLine.nodes?.find(v => v.ID === item.ID)
            if (node && (!node.nodeFiles || node.nodeFiles.length <= 0)){
                messageApi.warning("请先上传节点文件，再将节点状态修改为已完成~");
                return
            }
        }
        setFormList((prev) => {
            let newList = prev.map((item, idx) => {
                if (idx === index) {
                    return {
                        ...item,
                        [field]: value
                    }
                }
                return item
            })
            // 按时间排序
            if (field === "date") {
                newList = newList.sort((a, b) => {
                    if (a.date && !b.date) return -1
                    if (!a.date && b.date) return 1
                    if (a.date && b.date) {
                        return new Date(a.date).getTime() - new Date(b.date).getTime()
                    }
                    return 0
                })
                console.log(newList)
            }
            return newList
        })
    }
    const onAddNode = () => {
        setFormList((prev) => [
            ...prev,
            {
                nodeName: "",
                date: "",
                status: 0,
                isDraw: false,
                isDds: false
            }
        ])
    }
    const onRmoveNode = (index: number) => {
        setFormList((prev) => prev.filter((item, idx) => idx !== index))
    }
    
    const onCreateLine = () => {
        if (formList.length === 0) {
            messageApi.warning("请至少添加一个节点~");
            return
        }
        if (!currentNodeLine.lineName) {
            messageApi.warning("任务线名称不可为空~");
            return
        }
        // if (formList.find(v => !v.date)) {
        //     messageApi.warning("节点时间不可为空~");
        //     return
        // }
        const nodes = formList.map(v => {
            let nodes: any[] = [], pmNodes: any[] = []
            const { renderLinkNodes, ...item } = v
            renderLinkNodes?.forEach(n => {
                if (n.type === k_renderLineType.default) {
                    nodes.push({ ID: n.ID })
                } else {
                    pmNodes.push({ code: n.ID, type: n.type, lineId: n.lineId })
                }
            })
            return {
                ...item,
                nodes: nodes,
                pmNodes: pmNodes
            }
        })

        if (currentNodeLine.ID) {
            if(isUpdating) return
            updateControlNodeLine(currentNodeLine.ID, {
                lineName: currentNodeLine.lineName || "",
                assignUserId: currentNodeLine.assignUserId,
                colorValue: currentNodeLine.colorValue,
                nodes: nodes
            })
            return
        }
        if(isCreating) return
        createControlNodeLine(projectID, {
            lineName: currentNodeLine.lineName || "",
            assignUserId: currentNodeLine.assignUserId,
            colorValue: currentNodeLine.colorValue,
            nodes: nodes
        })
    }
    const onDeleteLine = (item: API.PROJECT.ControlNodeLine) => {
        if (!item.ID) {
            return
        }
        if(item.templateLineId) {
            messageApi.warning("系统模板任务线不允许删除");
            return
        }
        modalApi.confirm({
            title: null,
            icon: null,
            content: <span className="text-[13px]">确认删除<span className="font-bold px-1">{item.lineName}</span>任务线吗？</span>,
            okButtonProps: {
                danger: true,
                type: "primary"
            },
            onOk() {
                deleteControlNodeLine(item.ID).then(() => {
                    if (currentNodeLine.ID === item.ID) {
                        onChangeCreateTab()
                    }
                })
            }
        });
    }
    const linkNodeOptions = useMemo(() => {
        let list = props.list || []
        if(currentNodeLine.ID) {
            list = list.filter(v => v.ID !== currentNodeLine.ID)
        }
        let newlist: CascaderOption[] = list.map(v => {
            return {
                label: <div className="break-all max-w-[260px] whitespace-pre-wrap">{v.lineName}</div>,
                value: v.ID,
                type: k_renderLineType.default,
                children: v.nodes?.map(n => {
                    return {
                        label: <div className="break-all max-w-[260px] whitespace-pre-wrap">{n.nodeName}</div>,
                        value: n.ID,
                        type: k_renderLineType.default,
                    }
                })
            }
        })
        if (props.milestones && props.milestones.length > 0) {
            newlist.push({
                label: "标准里程碑",
                value: "milestone",
                type: k_renderLineType.milestone,
                children: props.milestones.map(v => {
                    return {
                        label: v.milestoneName,
                        value: v.milestoneCode,
                        type: k_renderLineType.milestone,
                    }
                })
            })
        }
        if (props.paymentLines && props.paymentLines.length > 0) {
            const paymentLines = props.paymentLines.map(item => {
                return {
                    label: <div className="break-all max-w-[260px] whitespace-pre-wrap">收款线-${item.contractName}</div>,
                    value: item.contractCode,
                    type: k_renderLineType.payment,
                    children: item.nodes.map(v => {
                        return {
                            label: <div className="break-all max-w-[260px] whitespace-pre-wrap">{v.description}</div>,
                            value: v.pmCode,
                            type: k_renderLineType.payment,
                        }
                    })
                }
            })
            newlist.push(...paymentLines)
        }
        return newlist

    }, [currentNodeLine, props.list, props.milestones, props.paymentLines])
   
    const { data: allUser = [], loading: userLoading, run: fetchAllUsers } = useRequest(getPlmProjectAllTeamUserApi, {
        defaultParams: [projectID],
    })

    const { run: updateControlLineSort } = useRequest(updateControlLineSortAPI, {
        manual: true,
        debounceWait: 30,
        onSuccess: (res) => {
            props.onUpdate()
        }
    })

    const [renderList, setRenderList] = useState<API.PROJECT.ControlNodeLine[]>([])
    const [tempRenderList, setTempRenderList] = useState<API.PROJECT.ControlNodeLine[] | null>(null);
    const curDdsNode = useRef<any>();
    const isDragging = useRef<boolean>(false);
    const [isDraggingItemID, setIsDraggingItemID] = useState<number | null>(null);
    const dragImageRef = useRef<any>(null);
    // 处理拖拽开始事件
    const handleDragStart = (e: React.DragEvent, index: number, itemID: number): void => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(index));
        const targetElement = e.target as HTMLElement;
        if (targetElement.parentElement) {
            e.dataTransfer.setDragImage(targetElement.parentElement, 20, 18)
        }
        isDragging.current = true;
        setIsDraggingItemID(itemID);
        
    };
    
    // 允许放置操作，阻止默认行为
    const handleDragOver = (e: React.DragEvent, overItemID: number, isUnsortableItem?: boolean, item?: any): void => {
        // 先执行e.preventDefault()，确保无论何种情况都能正确触发onDragEnd
        e.preventDefault();
        
        if(isUnsortableItem) {
            // 对于不可拖拽项，不执行后续拖拽逻辑，但已经阻止了默认行为
            // e.dataTransfer.dropEffect = 'none';
            return;
        }
        e.dataTransfer.dropEffect = 'move';
        
        // 更新拖拽悬停的索引
        if (isDraggingItemID !== null && isDraggingItemID !== overItemID) {
            const targetElement = e.currentTarget as HTMLElement;
            const rect = targetElement.getBoundingClientRect();
            const elementWidth = rect.width;
            const mouseY = e.clientY;
            const elementTop = rect.top;
            const mouseRelativeY = mouseY - elementTop;

            // 检查目标位置是否在任何不可拖拽项之前
            const listToUse = tempRenderList || renderList;
            const overIndex = listToUse.findIndex(item => item.ID === overItemID);
            if (overIndex === -1) {
                return
            }
            // 计算实际应该放置的索引：以元素高度一半为界限
            let actualDropIndex: number
            if (mouseRelativeY < elementWidth / 2) {
                // 鼠标位置低于元素高度一半，放在该元素上方
                actualDropIndex = overIndex;
            } else {
                // 鼠标位置高于元素高度一半，放在该元素下方
                actualDropIndex = overIndex + 1;
            }
            
            // 实时更新临时列表，显示新顺序
            const newTempList = [...listToUse];
            
            // 找到当前拖拽项在临时列表中的位置
            let currentDragIndex = newTempList.findIndex(item => item.ID === isDraggingItemID);
            if (currentDragIndex === null || currentDragIndex === -1) {
                return
            }
            
            // 确保实际放置索引在有效范围内
            actualDropIndex = Math.max(0, Math.min(actualDropIndex, newTempList.length));
            const dropItem = newTempList[actualDropIndex]
            if (dropItem.templateLine && dropItem.templateLine.isUnsortable) {
                return
            }
            // 如果当前拖拽项位置与实际放置位置不同，则重新排序
            if (currentDragIndex !== actualDropIndex) {
                const [movedItem] = newTempList.splice(currentDragIndex, 1);
                newTempList.splice(actualDropIndex, 0, movedItem);
                
                setTempRenderList(newTempList);
            }
        }
    };
    // 处理放置事件，重新排序列表项, dropItemID 放置ID
    const handleDrop = (e: React.DragEvent, dropItemID: number, isUnsortableItem?: boolean): void => {
        e.preventDefault();
        // 处理不可拖拽项的特殊放置逻辑
        if (isUnsortableItem || !isDragging.current || isDraggingItemID === null) {
            if (tempRenderList) {
                setRenderList(tempRenderList);
            }
            resetDragData()
            return
        } 

        // 计算实际放置位置，使用与handleDragOver相同的逻辑
        const targetElement = e.currentTarget as HTMLElement;
        const rect = targetElement.getBoundingClientRect();
        const elementHeight = rect.height;
        const mouseY = e.clientY;
        const elementTop = rect.top;
        const mouseRelativeY = mouseY - elementTop;
        
        let actualDropIndex: number;
        const listToUse = tempRenderList || renderList;
        const dropIndex = listToUse.findIndex(item => item.ID === dropItemID);
        if (dropIndex === -1) {
            resetDragData()
            return
        }
        if (mouseRelativeY < elementHeight / 2) {
            // 鼠标位置低于元素高度一半，放在该元素上方
            actualDropIndex = dropIndex;
        } else {
            // 鼠标位置高于元素高度一半，放在该元素下方
            actualDropIndex = dropIndex + 1;
        }
        
        // 使用最终的临时列表或创建新的渲染列表
        if (tempRenderList) {
            setRenderList(tempRenderList);
            updateControlLineSort(projectID, tempRenderList.map(item => item.ID))
        } else {
            // 确保实际放置索引在有效范围内
            actualDropIndex = Math.max(0, Math.min(actualDropIndex, renderList.length));
            
            // 找到拖拽项在原始列表中的位置
            const newRenderList = [...renderList];
            const draggedItemIndex = renderList.findIndex(item => item.ID === isDraggingItemID);
            if (draggedItemIndex === -1) {
                resetDragData()
                return
            }
            // 移除拖拽项并插入到新位置
            if (draggedItemIndex !== actualDropIndex) {
                const [movedItem] = newRenderList.splice(draggedItemIndex, 1);
                newRenderList.splice(actualDropIndex, 0, movedItem);
                setRenderList(newRenderList);
                updateControlLineSort(projectID, newRenderList.map(item => item.ID))
            }
        }
        
        resetDragData()
    };
    const resetDragData = () => {
        isDragging.current = false
        setIsDraggingItemID(null);
        setTempRenderList(null);
    }
    // 拖拽结束时重置状态
    const handleDragEnd = (): void => {
        resetDragData()
        updateControlLineSort(projectID, renderList.map(item => item.ID))
    };
    const onCloseModal = () => {
        setCurrentNodeLine({ ...initNodeLine })
        setFormList([])
        props.onClose()
    }
    const getLinkNodeLabel = (linkNode: RenderLinkNode) => {
        const line = linkNodeOptions.find(v => v.value === linkNode.lineId)
        const subNodes = line?.children?.find(n => n.value === linkNode.ID)
        if(!line || !subNodes) return ""
        return line.label + " - " + subNodes.label
    }
    const getStatusLabel = (status: number) => {
        const statusItem = props.allStatus.find(v => v.nodeStatus === status)
        return statusItem ? <span style={{ color: getNodeStatusColor(statusItem.name) }}>{statusItem.name}</span> : <></>
    }
    const getUserLabel = (userID: number) => {
        const user = allUser.find(v => v.id === userID)
        return user ? user.name : ""
    }
    const renderView = () => {
        return (
            <div>
                <div className="flex items-center text-xs mt-2">
                    <span className="whitespace-nowrap mr-2">任务线名称</span>
                    <span className="font-bold">{currentNodeLine.lineName}</span>
                    <span className="whitespace-nowrap ml-4 mr-2">指定人</span>
                    <span className="font-bold">{getUserLabel(currentNodeLine.assignUserId || 0)}</span>
                    
                    <span className="whitespace-nowrap ml-4 mr-2">任务线颜色</span>
                    <span className="w-4 h-4" style={{ backgroundColor: currentNodeLine.colorValue || "#000000" }}></span>
                    
                </div>
                <div className="mt-2">
                    <table className="text-xs w-full">
                        <colgroup>
                            <col />
                            <col />
                            <col />
                            <col />
                            <col className="w-16" />
                            <col className="w-16" />
                        </colgroup>
                        <tbody>
                            <tr>
                                <th className="whitespace-nowrap node-table-cell-head">节点</th>
                                <th className="whitespace-nowrap node-table-cell-head">时间</th>
                                <th className="whitespace-nowrap node-table-cell-head">关联</th>
                                <th className="whitespace-nowrap node-table-cell-head">状态</th>
                                <th className="whitespace-nowrap node-table-cell-head ">出图</th>
                                <th className="whitespace-nowrap node-table-cell-head">附件</th>
                            </tr>
                            {formList.map((item, index) => (
                                <tr key={index} className="mt-1">
                                    <td className="node-table-cell-row text-left">
                                        {item.nodeName}
                                    </td>
                                    <td className="node-table-cell-row whitespace-nowrap">
                                        {item.date}
                                    </td>
                                    <td className="node-table-cell-row">
                                        <div className="flex flex-wrap gap-1">
                                            {item.renderLinkNodes?.map(v => (
                                                <Tag>{getLinkNodeLabel(v)}</Tag>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="node-table-cell-row whitespace-nowrap">
                                        {getStatusLabel(item.status || 0)}
                                    </td>
                                    <td className="node-table-cell-row">
                                        {item.isDraw ? "是" : ""}
                                    </td>
                                    <td className="node-table-cell-row">
                                        {item.isDds ? "是" : ""}
                                    </td>
                                    {/* <td className="node-table-cell-row">
                                        {item.hasDDSFile && (
                                            <Tooltip title="已上传文件">
                                                <span className="text-black ml-1 text-[14px] p-1 hover:bg-gray-100 cursor-pointer" onClick={() => onDDSFileClick(item)}>
                                                    <CloudSvg />
                                                </span>
                                            </Tooltip>
                                        )}
                                    </td> */}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {formList.length === 0 && (
                        <div className="text-center text-gray-400 py-4">暂无数据</div>
                    )}
                </div>
            </div>
        )
    }
    const renderEdit = () => {
        const isStandardMilestone = currentNodeLine.isMainMilestone || currentNodeLine.isMilestone
        return (
            <div>
                <div className="flex items-center text-xs mt-2">
                    <span className="whitespace-nowrap mr-2">任务线名称</span>
                    <Input value={currentNodeLine.lineName} disabled={isStandardMilestone} onChange={(e) => setCurrentNodeLine(prev => ({ ...prev, lineName: e.target.value }))} />
                    {isStandardMilestone && (
                        <button className="border border-solid border-gray-700 rounded-md text-gray-700 bg-white hover:bg-gray-700 hover:text-white text-xs px-2 py-1 whitespace-nowrap ml-4"
                            onClick={() => updateControlNodeLineMilestoneTime(currentNodeLine.ID || 0)}>同步里程碑时间</button>
                    )}
                    <span className="whitespace-nowrap ml-4 mr-2">指定人</span>
                    <Select className="min-w-[100px]"
                        disabled={isStandardMilestone}
                        value={currentNodeLine.assignUserId || null}
                        onChange={(e) => setCurrentNodeLine(prev => ({ ...prev, assignUserId: e }))}
                        options={allUser?.map(v => ({
                            value: v.id,
                            label: (
                                <div className="flex items-center">
                                    <div className="w-[30px] h-[30px] leading-[30px] text-[12px] rounded-full text-white whitespace-nowrap scale-75 text-center"
                                        style={{ background: toHsl(v.name) }}>{v.name}</div>
                                    <span className="text-xs">{v.name}</span>
                                </div>
                            )

                        }))}
                        styles={{ popup: { root: { width: "fit-content", minWidth: 150 } } }}
                        placeholder="选择指定人"
                    />
                    {!currentNodeLine.isMainMilestone && (
                        <>
                        <span className="whitespace-nowrap ml-4 mr-2">任务线颜色</span>
                        <Input type="color" className="w-8 h-8 p-0 shrink-0" value={currentNodeLine.colorValue || "#000000"}
                                onChange={(e) => setCurrentNodeLine(prev => ({ ...prev, colorValue: e.target.value }))} />
                        </>
                    )}
                    
                    {currentNodeLine.templateLineId && (
                        <Button size="small" className="ml-4" onClick={() => onImportTemplate(currentNodeLine.templateLineId)}>导入模板</Button>
                    )}
                </div>
                <div className="mt-2">
                    <table className="text-xs w-full">
                        <tbody>
                            <tr>
                                <th></th>
                                <th className="whitespace-nowrap pb-1">节点</th>
                                <th className="whitespace-nowrap pb-1">时间</th>
                                <th className="whitespace-nowrap pb-1">关联</th>
                                <th className="whitespace-nowrap pb-1">状态</th>
                                <th className="whitespace-nowrap px-1 pb-1">出图</th>
                                <th className="whitespace-nowrap px-1 pb-1">附件</th>
                                <th></th>
                            </tr>
                            {formList.map((item, index) => (
                                <tr key={index} className="mt-1">
                                    <td className="py-1">
                                        {!isStandardMilestone && (
                                            <span onClick={() => onRmoveNode(index)} className="flex text-[20px] text-gray-400 cursor-pointer hover:text-gray-600">
                                                <RemoveSvg />
                                            </span>
                                        )}
                                        
                                    </td>
                                    <td className="py-1">
                                        <Input
                                            placeholder="请输入节点名称"
                                            value={item.nodeName}
                                            disabled={isStandardMilestone}
                                            onChange={(e) => onFormChange("nodeName", e.target.value, index)}

                                        />
                                    </td>
                                    <td className="py-1">
                                        <DatePicker className="text-xs w-full" placeholder="请选择时间"
                                            value={item.date && dayjs(item.date)}
                                            disabled={isStandardMilestone}
                                            onChange={(date, dateString) => onFormChange("date", dateString, index)}
                                        />
                                    </td>
                                    <td className="py-1 min-w-[100px] max-w-[260px] whitespace-pre-wrap text-ellipsis overflow-hidden">
                                        <Cascader
                                            className="w-full"
                                            showCheckedStrategy="SHOW_CHILD"
                                            value={item.renderLinkNodes?.map(v => [v.lineId!, v.ID])}
                                            onChange={(vals, opts) => {
                                                let paramsIDs: RenderLinkNode[] = []
                                                opts.forEach(v => {
                                                    if (v.length > 1) {
                                                        paramsIDs.push({ lineId: v[0].value, ID: v[1].value, type: v[0].type })
                                                    }
                                                })
                                                onFormChange("renderLinkNodes", paramsIDs, index)
                                            }} multiple placeholder="选择节点" options={linkNodeOptions} />
                                    </td>
                                    <td className="py-1">
                                        <Select className="w-full" value={item.status} onChange={(val) => onFormChange("status", val, index, item)} placeholder="状态"
                                            styles={{ popup: { root: { width: 100 } } }}
                                            disabled={isStandardMilestone}
                                            options={props.allStatus?.map(v => ({ label: <span style={{ color: getNodeStatusColor(v.name) }}>{v.name}</span>, value: v.nodeStatus }))} />
                                    </td>
                                    <td className="py-1">
                                        <div className="flex justify-center">
                                            <Checkbox checked={item.isDraw} onChange={(e) => onFormChange("isDraw", e.target.checked, index)}></Checkbox>
                                        </div>

                                    </td>
                                    <td className="py-1">
                                        <div className="flex justify-center items-center">
                                            <Checkbox checked={item.isDds} onChange={(e) => onFormChange("isDds", e.target.checked, index)}></Checkbox>
                                        </div>
                                    </td>
                                    <td className="py-1">
                                        {item.hasDDSFile && (
                                            <Tooltip title="已上传文件">
                                                <span className="text-black ml-1 text-[14px] p-1 hover:bg-gray-100 cursor-pointer" onClick={() => onDDSFileClick(item)}>
                                                    <CloudSvg />
                                                </span>
                                            </Tooltip>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="flex justify-between mt-4 items-center select-none">
                    {!isStandardMilestone ? (
                        <Button onClick={onAddNode}>新增节点</Button>
                    ) : <div></div>}
                    <div className="flex">
                        <Button onClick={props.onClose}>取消</Button>
                        <Button className="ml-2" type="primary" onClick={onCreateLine}>{currentNodeLine.ID ? "保存" : "创建"}</Button>
                    </div>

                </div>
            </div>
        )
    }
    return (
        <ConfigProvider
            theme={{
                token: {
                    /* 这里是你的全局 token */
                    fontSize: 12
                },
            }}
        >
            {modalContextHolder}
            {contextHolder}
            <Modal open={props.visible} footer={null} maskClosable={false} destroyOnHidden onCancel={onCloseModal} width="fit-content" style={{minWidth: 520}}>
                <div>
                    <div className="border-0 border-b border-solid border-gray-200 overflow-x-auto overflow-y-hidden pb-3 py-2 w-[calc(100%-20px)]">
                        <div className="flex gap-1 pr-4">
                            {(tempRenderList || renderList).map((item, index) => {
                                // 确定当前项是否为正在拖拽的项
                                const isDraggedItem = isDragging.current && isDraggingItemID !== null && isDraggingItemID === item.ID
                                // 检查该项是否不可拖拽
                                const isUnsortableItem = (!!item.templateLine && item.templateLine.isUnsortable) || props.forbidEdit || (item.isMainMilestone && item.isMilestone);

                                return (
                                    <React.Fragment key={`${item.ID}`}>
                                        <div onClick={() => onChangeTab(item)} key={index}
                                            className={clsx("relative group text-xs px-4 py-2 flex items-center whitespace-nowrap cursor-pointer rounded-sm transition-all duration-200",
                                                isDraggedItem ? 'opacity-50' : '',
                                                currentNodeLine.ID === item.ID ? "bg-dark-bg text-white hover:bg-dark-bg" : "bg-white text-gray-800 hover:bg-gray-100")}
                                            onDragOver={(e) => handleDragOver(e, item.ID, isUnsortableItem, item)}
                                            onDrop={(e) => handleDrop(e, item.ID, isUnsortableItem)}
                                            onDragEnd={handleDragEnd}
                                        >
                                            {!isUnsortableItem && (
                                                <span onClick={(e) => e.stopPropagation()} className="invisible group-hover:visible cursor-move text-[20px]"
                                                    draggable={!isUnsortableItem}
                                                    onDragStart={(e) => {
                                                        if (!isUnsortableItem) {
                                                            // 找到该项在原始列表中的索引
                                                            handleDragStart(e, index, item.ID)
                                                        }
                                                    }}
                                                >
                                                    <DragSvg />
                                                </span>
                                            )}
                                            
                                            
                                            {item.isMainMilestone ? `${item.lineName}(标准里程碑)` : item.lineName}
                                            {!item.isMainMilestone && !props.forbidEdit && !item.templateLineId && (
                                                <div onClick={(e) => { e.stopPropagation(); onDeleteLine(item); }} className="hidden hover:bg-gray-400 group-hover:flex bg-gray-300 text-gray-700 absolute -right-2 -top-2 z-10 p-[3px] text-[12px] rounded-full">
                                                    <CloseSvg />
                                                </div>
                                            )}

                                        </div>
                                    </React.Fragment>
                                )
                            })}
                            {props.forbidEdit ? null : (
                                <div onClick={onChangeCreateTab}
                                    className={clsx("text-xs px-4 py-2 whitespace-nowrap sticky right-0 z-20  cursor-pointer", !currentNodeLine.ID ? "bg-dark-bg text-white hover:bg-dark-bg" : " bg-white text-gray-800 hover:bg-gray-100")}>+ 新建</div>
                            )}
                        </div>
                    </div>
                    {props.forbidEdit ? renderView() : renderEdit()}
                </div>
            </Modal>
        </ConfigProvider>
    )
}
export default Index